import { SeamComponent } from '@seddi/scene';

import { type EntityRenderable, updateTransform } from '../renderables/entityRenderable';
import { create as createLight } from '../renderables/lightRenderable';
import { create as createMesh } from '../renderables/meshRenderable';
import { create as createModel } from '../renderables/modelRenderable';
import { create as createPiece } from '../renderables/pieceRenderable';
import { create as createSeam } from '../renderables/seamRenderable';
import type { TSceneEntityEx } from '../scene/sceneTypes';
import { update as updateLight } from './lightReconciler';
import { update as updateMesh } from './meshReconciler';
import { update as updateMeshRenderable } from './meshRenderableReconciler';
import { update as updateModel } from './modelReconciler';
import { update1st as updatePiece1st, update2nd as updatePiece2nd } from './pieceReconciler';
import type { SceneReconcilerFrame, SceneReconcilerFrameEntity } from './sceneReconcilerFrame';
import { update as updateSeam } from './seamReconciler';

/**
 * Update entity 1st pass.
 * @param renderable - Entity renderable.
 * @param nextProps Next entity state.
 * @param prevProps Prev entity state.
 */
export const update1st = (
  renderable: EntityRenderable,
  nextProps: TSceneEntityEx,
  prevProps: TSceneEntityEx | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  frame.nextParent = nextProps;
  frame.prevParent = prevProps;
  const nextComponents = nextProps.components;
  const prevComponents = prevProps?.components;
  let dirty = false;

  const entity = { data: nextProps } as SceneReconcilerFrameEntity;
  frame.entities.set(nextProps._id, entity);

  if (nextComponents !== prevComponents) {
    // do transform 3D
    if (prevComponents?.transform3D !== nextComponents.transform3D) {
      updateTransform(renderable, nextComponents.transform3D);
      dirty = true;
    }
    // do garment
    if (nextComponents.garment !== prevComponents?.garment) {
      const config = frame.config;
      const garmentGeometry = nextComponents.garmentGeometry;

      // disable sitches textures if urls are not provided
      config.useStitchesDecals =
        config.useStitchesDecals &&
        Boolean((garmentGeometry as any).stitchesTexturesUrl) &&
        Boolean((garmentGeometry as any).stitchesTexturesBinaryUrl);

      // load stitches textures
      if (config.useStitchesDecals) {
        const resources = frame.resources;
        const id = nextProps._id;
        const stitchTextures = resources.findStitchesTextures(id);
        if (stitchTextures) {
          frame.stitchesTextures = stitchTextures;
        } else {
          if (!resources.hasStitchesTexturesRequest(id)) {
            const desc = {
              id,
              url: (garmentGeometry as any).stitchesTexturesUrl,
              urlBinary: (garmentGeometry as any).stitchesTexturesBinaryUrl,
            };
            resources.loadStitchesTexturesDeferred(desc);
          }
        }
      }

      // TODO: load garment geometry
      // if (config.useGarmentGeometry) {
      //   frame.garmentGeometry = nextScene.hiResGeometry;
      // }
    }
    // do piece
    if (nextComponents.garmentPiece !== prevComponents?.garmentPiece) {
      entity.isPiece = true;

      let piece = renderable.piece;
      if (!piece) {
        piece = createPiece(renderable.id, renderable.node);
        renderable.piece = piece;
        piece.mesh.frustumCulled = frame.config.frustumCulling;
      }
      dirty =
        updatePiece1st(piece, nextComponents.garmentPiece, prevComponents?.garmentPiece, frame) ||
        dirty;
    }
    // do light
    if (nextComponents.light !== prevComponents?.light) {
      const light =
        renderable.light ?? createLight(renderable.id, renderable.node, nextComponents.light.kind);
      renderable.light = light;
      dirty = updateLight(light, nextComponents.light) || dirty;
    }
    // do mesh
    if (nextComponents.mesh !== prevComponents?.mesh) {
      let mesh = renderable.mesh;
      if (!mesh) {
        createMesh(renderable.id, renderable.node, nextProps.name);
        mesh.mesh.frustumCulled = frame.config.frustumCulling;
        renderable.mesh = mesh;
      }
      dirty = updateMesh(mesh, nextComponents.mesh, prevComponents?.mesh, frame) || dirty;
    }
    // do mesh renderable
    if (nextComponents.meshRenderable !== prevComponents?.meshRenderable) {
      const mesh = renderable.mesh ?? createMesh(renderable.id, renderable.node, nextProps.name);
      renderable.mesh = mesh;
      dirty =
        updateMeshRenderable(
          mesh,
          nextComponents.meshRenderable,
          prevComponents?.meshRenderable,
          frame,
        ) || dirty;
    }
    // do model
    if (nextComponents.model !== prevComponents?.model) {
      let model = renderable.model;
      if (!model) {
        model = createModel(renderable.id, renderable.node);
        renderable.model = model;
      }
      dirty = updateModel(model, nextComponents.model, prevComponents?.model, frame) || dirty;
    }

    if (nextComponents) {
      renderable.node.userData = nextComponents;
    }
  }

  // Update frame cleanup.
  frame.nextParent = undefined;
  frame.prevParent = undefined;

  return dirty;
};

/**
 * Update entity 2nd pass.
 * @param renderable - Entity renderable.
 * @param nextProps Next entity state.
 * @param prevProps Prev entity state.
 */
export const update2nd = (() => {
  const _pieces = <string[]>[];

  return (
    renderable: EntityRenderable,
    nextProps: TSceneEntityEx,
    prevProps: TSceneEntityEx | undefined,
    frame: SceneReconcilerFrame,
  ): boolean => {
    // Update frame.
    const nextScene = frame.nextScene;
    const prevScene = frame.prevScene;
    frame.nextParent = nextProps;
    frame.prevParent = prevProps;

    const nextComponents = nextProps.components;
    const prevComponents = prevProps?.components;
    let dirty = false;

    // buttons placement depends on all pieces visited first (holes)
    if (nextComponents.garmentPiece) {
      // update trigger restrictions?
      const piece = renderable.piece;
      dirty =
        updatePiece2nd(piece, nextComponents.garmentPiece, prevComponents?.garmentPiece, frame) ||
        dirty;
    }

    // do seam
    if (nextComponents.garmentSeam) {
      // geometry update triggered by piece geometry updates
      let isGeometryDirty =
        !prevScene || !nextScene ? false : nextScene.hiResGeometry !== prevScene.hiResGeometry;

      if (!isGeometryDirty) {
        _pieces.length = 0;
        SeamComponent.Pieces(nextComponents.garmentSeam, _pieces);
        isGeometryDirty = _pieces.some((piece) => frame.entities.get(piece)?.isGeometryDirty);
      }

      frame.entities.get(nextProps._id).isGeometryDirty = isGeometryDirty;

      if (nextComponents.garmentSeam !== prevComponents?.garmentSeam || isGeometryDirty) {
        let seam = renderable.seam;
        if (!seam) {
          seam = createSeam(
            renderable.id,
            renderable.node,
            nextComponents.garmentSeam,
            frame.theme,
            frame.config.useStitchesDecals,
          );
          seam.mesh.frustumCulled = frame.config.frustumCulling;
          renderable.seam = seam;
        }
        dirty =
          updateSeam(seam, nextComponents.garmentSeam, prevComponents?.garmentSeam, frame) || dirty;
      }
    }

    // Update frame cleanup.
    frame.nextParent = undefined;
    frame.prevParent = undefined;

    return dirty;
  };
})();
